import React from 'react';
import {
  Smile,
  AlignLeft,
  Sparkles,
  Shield,
  Baby,
  HeartPulse,
  ArrowRight,
} from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { services } from '../data/mock';

const iconMap = {
  braces: Smile,
  aligners: AlignLeft,
  ceramic: Sparkles,
  retainer: Shield,
  child: Baby,
  surgery: HeartPulse,
};

const Services = () => {
  return (
    <section id="services" className="py-24 bg-white relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="inline-block px-4 py-2 bg-teal-50 text-teal-700 font-semibold text-sm rounded-full mb-4">
            Our Services
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-6">
            Comprehensive Orthodontic
            <span className="text-gradient"> Solutions</span>
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            From traditional braces to cutting-edge clear aligners, we offer a full 
            range of treatments tailored to your unique smile goals.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon] || Smile;
            return (
              <Card
                key={service.id}
                className={`group border-0 shadow-lg hover:shadow-2xl card-hover bg-white animate-fade-in-up`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-8">
                  {/* Icon */}
                  <div className="service-icon w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="w-8 h-8 text-teal-600" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-slate-800 mb-3 group-hover:text-teal-600 transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 mb-5 leading-relaxed">
                    {service.description}
                  </p>

                  {/* Features */}
                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature) => (
                      <li
                        key={feature}
                        className="flex items-center gap-2 text-sm text-slate-500"
                      >
                        <div className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  {/* Learn More Link */}
                  <a
                    href="#contact"
                    className="inline-flex items-center gap-2 text-teal-600 font-semibold hover:gap-3 transition-all duration-200"
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA Banner */}
        <div className="mt-20 bg-slate-50 rounded-3xl p-8 lg:p-12 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-teal-100/50 rounded-full blur-3xl" />
          <div className="relative flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="text-2xl lg:text-3xl font-bold text-slate-800 mb-3">
                Not Sure Which Treatment Is Right for You?
              </h3>
              <p className="text-slate-600 text-lg">
                Schedule a free consultation and let our experts help you choose 
                the perfect treatment plan.
              </p>
            </div>
            <a
              href="#appointment"
              className="shrink-0 inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white font-semibold px-8 py-4 rounded-xl btn-transition shadow-lg shadow-teal-600/20"
            >
              Get Free Consultation
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
